import api from 'api'
import { NavigateFunction } from 'react-router-dom'
import { TFunction } from 'i18next'
import { store } from '../../store'
import { Routes } from '../../routes'
import { NotifierType } from '../../components/Notifier'
import { UseNotifierProps } from '../../hooks/useNotifier'
import { priceListsTableActions } from './PriceListsTable/store/price-lists-table-slice'
import { priceListFormActions } from './PriceListForm/store/price-list-form-slice'
import {
  buildDoubledHeaders,
  defaultValueColumns,
  getInvalidPeriodRows,
  isDateColumn,
  isDefaultPeriodRow,
  isPositiveFloatCell,
  isValidDateCell,
  makeBlankRow,
  PriceListFormModel,
  PricingGrid,
  pricingsToGrid,
} from './models/price-list-form-slice.model'
import { PriceListTableItem } from './models/price-lists-table-slice.model'

// A new price list starts with just the default period row (no dates), which
// acts as the fallback price for any not-configured period. The value columns
// are the full media_format / offer matrix, each doubled into an impressions
// and an audience cell that share a single pair of date columns per period.
const blankGrid = (): PricingGrid => {
  const headers = buildDoubledHeaders(defaultValueColumns())

  return {
    headers,
    content: [makeBlankRow(headers)],
  }
}

const hasValidCells = (grid: PricingGrid): boolean =>
  grid.content.every((row, rowIndex) =>
    row.every((cell, col) => {
      if (isDateColumn(grid.headers[col])) {
        // The default period row is allowed to have empty dates.
        return isDefaultPeriodRow(rowIndex) ? true : isValidDateCell(cell)
      }
      return isPositiveFloatCell(cell)
    })
  )

const getValidationError = (priceList: PriceListFormModel): string | null => {
  const grid = priceList.pricing

  if (!hasValidCells(grid)) {
    return 'priceLists.invalidValues'
  }

  if (getInvalidPeriodRows(grid).size > 0) {
    return 'priceLists.overlappingPeriods'
  }

  return null
}

export default class PriceListsService {
  static getPriceLists = (pageNumber: number, quantityPerPage: number): void => {
    store.dispatch(priceListsTableActions.setLoading(true))
    api.priceLists
      .getPriceLists({
        first: pageNumber * quantityPerPage,
        last: quantityPerPage,
      })
      .then(res => {
        const { pricingPlans } = res.data
        const priceLists: PriceListTableItem[] = pricingPlans.nodes.map(node => ({
          id: node.id,
          name: node.name,
          pricing: pricingsToGrid(node.pricings),
        }))
        store.dispatch(priceListsTableActions.setPriceLists(priceLists))
        store.dispatch(priceListsTableActions.setTotalCount(pricingPlans.totalCount))
      })
      .finally(() => {
        store.dispatch(priceListsTableActions.setLoading(false))
        store.dispatch(priceListsTableActions.setLoaded(true))
      })
  }

  static getPriceList = (id: string): void => {
    store.dispatch(priceListFormActions.setLoading(true))
    api.priceLists
      .getPriceList(id)
      .then(res => {
        const node = res.data.node
        if (node?.id) {
          store.dispatch(
            priceListFormActions.setPriceListForm({
              id: node.id,
              name: node.name,
              pricing: pricingsToGrid(node.pricings),
            })
          )
        }
      })
      .finally(() => {
        store.dispatch(priceListFormActions.setLoading(false))
      })
  }

  static loadTemplate = (): void => {
    store.dispatch(priceListFormActions.resetPriceListForm())
    store.dispatch(priceListFormActions.setGrid({ pricing: blankGrid() }))
  }

  static handleSubmit = (
    navigate: NavigateFunction,
    addNotification: UseNotifierProps['addNotification'],
    t: TFunction,
    id?: string
  ): Promise<void> => {
    const priceListForm: PriceListFormModel = store.getState().priceListForm

    const validationError = getValidationError(priceListForm)
    if (validationError) {
      addNotification(NotifierType.ERROR, t(validationError))
      return Promise.resolve()
    }

    store.dispatch(priceListFormActions.setLoading(true))

    const apiCall = id
      ? api.priceLists.updatePriceList(priceListForm)
      : api.priceLists.createPriceList(priceListForm)

    return apiCall
      .then(() => {
        navigate(Routes.PRICE_LISTS.ALL)
        addNotification(
          NotifierType.SUCCESS,
          id ? t('notification.updatePriceListSuccess') : t('notification.addPriceListSuccess')
        )
      })
      .catch(() => void {})
      .finally(() => {
        store.dispatch(priceListsTableActions.resetPriceLists())
        store.dispatch(priceListFormActions.setLoading(false))
      })
  }

  static removePriceList = (priceList: PriceListTableItem): Promise<unknown> =>
    api.priceLists
      .removePriceList({ id: priceList.id })
      .catch(() => void {})
      .finally(() => store.dispatch(priceListsTableActions.setLoaded(false)))
}
